import { useEffect, useState } from "react";
import { api, API } from "../../lib/api";
import { Plus, Pencil, Trash2, FileText, Download, X, Send, Users } from "lucide-react";

const TYPE_LABELS = {
  text: "Testo", number: "Numero", date: "Data", importo: "Importo (€)",
  checkbox: "Casella", radio: "Scelta multipla", select: "Menu a tendina", textarea: "Area di testo",
};

function fmtDate(iso) { try { return new Date(iso).toLocaleString("it-IT"); } catch { return iso; } }

function TemplateEditor({ initial, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [category, setCategory] = useState(initial?.category || "Altri Documenti");
  const [fields, setFields] = useState(initial?.fields || []);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNew = !initial;

  const addField = () => setFields([...fields, { label: "", key: "", type: "text", required: false }]);
  const upd = (i, v) => { const c = [...fields]; c[i] = v; setFields(c); };
  const rm = (i) => setFields(fields.filter((_, idx) => idx !== i));
  const mv = (i, j) => { const c = [...fields]; [c[i], c[j]] = [c[j], c[i]]; setFields(c); };

  const save = async () => {
    if (!title.trim()) { alert("Titolo obbligatorio"); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem("bg_token");
      if (isNew) {
        const form = new FormData();
        form.append("payload", JSON.stringify({ title, description, category, fields }));
        if (pdfFile) form.append("pdf", pdfFile);
        const res = await fetch(`${API}/admin/form-templates`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: form });
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Errore"); }
      } else {
        await api.put(`/admin/form-templates/${initial.id}`, { title, description, category, fields });
      }
      onSaved();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-4">
      <h4 className="font-display font-bold text-lg text-[#1A1A1A]">{isNew ? "Nuovo modello" : "Modifica modello"}</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Titolo *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" placeholder="Scheda Aziendale — SRH01" />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Categoria</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
        <div className="md:col-span-3">
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Descrizione</label>
          <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-sm" />
        </div>
      </div>
      {isNew && (
        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-1">Modello PDF (opzionale)</label>
          <input type="file" accept="application/pdf" onChange={(e) => setPdfFile(e.target.files?.[0] || null)} className="text-sm" />
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#007A3D]">Campi ({fields.length})</div>
          <button onClick={addField} className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#EAF8F0] text-[#007A3D]"><Plus className="w-3.5 h-3.5 inline" /> Aggiungi campo</button>
        </div>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={i} className="border border-[#E5E7EB] rounded-xl p-2 flex gap-2 items-start flex-wrap">
              <input className="flex-1 min-w-[180px] rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-sm" placeholder="Etichetta" value={f.label || ""} onChange={(e) => upd(i, { ...f, label: e.target.value, key: f.key || e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_") })} />
              <input className="w-32 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-mono" placeholder="chiave" value={f.key || ""} onChange={(e) => upd(i, { ...f, key: e.target.value })} />
              <select className="rounded-lg border border-[#E5E7EB] px-2 py-1.5 text-sm bg-white" value={f.type} onChange={(e) => upd(i, { ...f, type: e.target.value })}>
                {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <label className="inline-flex items-center gap-1 text-xs px-2"><input type="checkbox" checked={!!f.required} onChange={(e) => upd(i, { ...f, required: e.target.checked })} />Obbl.</label>
              <button onClick={() => mv(i, Math.max(0, i - 1))} className="p-1 text-xs">↑</button>
              <button onClick={() => mv(i, Math.min(fields.length - 1, i + 1))} className="p-1 text-xs">↓</button>
              <button onClick={() => rm(i)} className="p-1 text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-4 py-2 text-sm rounded-full border border-[#E5E7EB]">Annulla</button>
        <button onClick={save} disabled={saving} className="px-5 py-2 text-sm rounded-full bg-[#007A3D] text-white disabled:opacity-60">{saving ? "Salvataggio…" : "Salva modello"}</button>
      </div>
    </div>
  );
}

function AssignModal({ template, onClose, onAssigned }) {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [assigning, setAssigning] = useState(false);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data || [])); }, []);
  const toggle = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const assign = async () => {
    if (selected.size === 0) { alert("Seleziona almeno un cliente"); return; }
    setAssigning(true);
    try {
      const { data } = await api.post(`/admin/form-templates/${template.id}/assign`, { client_ids: Array.from(selected) });
      alert(`Assegnato a ${data.count} cliente${data.count > 1 ? 'i' : ''}. Ora possono compilarlo dalla loro area riservata.`);
      onAssigned();
    } catch (e) { alert(e.response?.data?.detail || "Errore"); }
    finally { setAssigning(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-6 m-4 shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.15em] text-[#007A3D]">Assegna modello</div>
            <h4 className="font-display font-bold text-lg text-[#1A1A1A]">{template.title}</h4>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F9FAFB]"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-[#6B7280] mb-3">Seleziona i clienti a cui vuoi assegnare questo modulo. Una copia editabile apparirà nella loro Area Riservata.</p>
        <div className="flex-1 overflow-y-auto border border-[#E5E7EB] rounded-xl divide-y divide-[#F3F4F6]">
          {clients.length === 0 && <div className="p-6 text-sm text-[#6B7280] text-center">Nessun cliente. Creane uno prima.</div>}
          {clients.map((c) => (
            <label key={c.id} data-testid={`assign-client-${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9FAFB] cursor-pointer">
              <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} className="w-4 h-4 accent-[#00A651]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-[#1A1A1A]">{c.name || c.username}</div>
                <div className="text-xs text-[#6B7280] font-mono">@{c.username}{!c.active && " · DISATTIVATO"}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-full border border-[#E5E7EB]">Annulla</button>
          <button data-testid="confirm-assign" onClick={assign} disabled={assigning || selected.size === 0} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white disabled:opacity-50">
            <Send className="w-4 h-4" /> Assegna a {selected.size} cliente{selected.size !== 1 ? 'i' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FormTemplatesManager() {
  const [tpls, setTpls] = useState([]);
  const [view, setView] = useState("list");
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const load = () => api.get("/admin/form-templates").then((r) => setTpls(r.data || []));
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    if (!confirm(`Eliminare il modello "${t.title}"? (Non tocca i moduli già assegnati ai clienti)`)) return;
    await api.delete(`/admin/form-templates/${t.id}`); load();
  };
  const dlPdf = async (t) => {
    const token = localStorage.getItem("bg_token");
    const res = await fetch(`${API}/admin/form-templates/${t.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = t.pdf_filename || "modello.pdf"; document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (view === "new") return <TemplateEditor onSaved={() => { setView("list"); load(); }} onCancel={() => setView("list")} />;
  if (view === "edit" && editing) return <TemplateEditor initial={editing} onSaved={() => { setView("list"); setEditing(null); load(); }} onCancel={() => { setView("list"); setEditing(null); }} />;

  return (
    <div className="space-y-4" data-testid="templates-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl text-[#1A1A1A]">Modelli compilabili</h3>
          <p className="text-sm text-[#6B7280]">Crea modelli riutilizzabili e assegnali ai clienti quando vuoi. Ogni assegnazione crea una copia editabile solo per quel cliente.</p>
        </div>
        <button data-testid="new-template" onClick={() => setView("new")} className="inline-flex items-center gap-1.5 px-5 py-2 text-sm rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><Plus className="w-4 h-4" /> Nuovo modello</button>
      </div>
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
        {tpls.length === 0 ? (
          <div className="p-10 text-center text-[#6B7280]">Nessun modello ancora creato.</div>
        ) : (
          <ul className="divide-y divide-[#F3F4F6]">
            {tpls.map((t) => (
              <li key={t.id} data-testid={`tpl-row-${t.id}`} className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1A1A1A]">{t.title}</div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{t.category} · {t.fields?.length || 0} campi{t.has_template_pdf && " · PDF allegato"} · agg. {fmtDate(t.updated_at)}</div>
                  {t.description && <div className="text-xs text-[#6B7280] mt-1 line-clamp-2">{t.description}</div>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`assign-tpl-${t.id}`} onClick={() => setAssigning(t)} className="inline-flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-full bg-[#007A3D] hover:bg-[#00A651] text-white"><Users className="w-3.5 h-3.5" /> Assegna a cliente</button>
                  {t.has_template_pdf && <button onClick={() => dlPdf(t)} title="Scarica PDF" className="p-2 rounded-full hover:bg-[#F9FAFB]"><Download className="w-4 h-4" /></button>}
                  <button onClick={() => { setEditing(t); setView("edit"); }} title="Modifica" className="p-2 rounded-full hover:bg-[#F9FAFB]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(t)} title="Elimina" className="p-2 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {assigning && <AssignModal template={assigning} onClose={() => setAssigning(null)} onAssigned={() => { setAssigning(null); }} />}
    </div>
  );
}
